import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  adminListInstallmentPlans,
  adminRecordInstallmentPayment,
  adminUpdateInstallmentPlanStatus,
  adminUpdateInstallmentPlan,
  adminListPendingClaims,
  adminVerifyClaim,
  adminRejectClaim,
  adminListSendJobs,
  adminRetrySendJob,
  adminGetPlanInvoiceUrl,
  adminResendPlanInvoice,
  adminCreateManualClientPlan,
  adminGetInstallmentPlan,
} from "@/lib/api/brand-installments.functions";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Wallet, Link as LinkIcon, FileText, RefreshCw, Mail, Pencil, History } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/brand-installments")({
  head: () => ({
    meta: [
      { title: "Brand installment plans | Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Page,
});

function Page() {
  const fetchList = useServerFn(adminListInstallmentPlans);
  const recordPay = useServerFn(adminRecordInstallmentPayment);
  const setStatus = useServerFn(adminUpdateInstallmentPlanStatus);
  const resendInvoice = useServerFn(adminResendPlanInvoice);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-brand-installments"],
    queryFn: () => fetchList(),
    // Poll every 15s instead of realtime (brand_installment tables are no longer
    // in the realtime publication for security hardening).
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const [amount, setAmount] = useState("50");
  const [method, setMethod] = useState<"eft" | "cash" | "payfast" | "other">("eft");
  const [note, setNote] = useState("");
  const [editPlan, setEditPlan] = useState<any | null>(null);
  const [historyPlan, setHistoryPlan] = useState<any | null>(null);


  const pay = useMutation({
    mutationFn: (v: { plan_id: string; amount_zar: number; method: any; note: string }) =>
      recordPay({ data: v }),
    onSuccess: () => {
      toast.success("Payment recorded");
      const recorded = plans.find((p: any) => p.id === openId);
      if (recorded) setHistoryPlan(recorded);
      setOpenId(null);
      setNote("");
      qc.invalidateQueries({ queryKey: ["admin-brand-installments"] });
      qc.invalidateQueries({ queryKey: ["admin-brand-installment-detail"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const statusMut = useMutation({
    mutationFn: (v: { id: string; status: any }) => setStatus({ data: v }),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["admin-brand-installments"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });
  const resendMut = useMutation({
    mutationFn: (v: { plan_id: string }) => resendInvoice({ data: v }),
    onSuccess: (r: any) => {
      toast.success(
        `Invoice re-sent to ${r?.sentTo ?? "recipient"}${r?.adminCopyTo ? ` (admin copy to ${r.adminCopyTo})` : ""}`,
      );
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to re-send"),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const plans = data?.plans ?? [];

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-navy" />
          <h2 className="font-display text-xl text-navy">Brand installment plans</h2>
        </div>
        <ManualClientButton />
      </header>
      <p className="text-xs text-muted-foreground">
        Weekly EFT instalments (min R50/week scheduled, but clients may pay any amount from R10).
        Record each payment as you receive it; the plan automatically moves to <strong>Settled</strong> once the balance reaches zero.
      </p>

      {plans.length === 0 ? (
        <p className="text-sm text-muted-foreground">No plans yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Ref</th>
                <th className="px-3 py-2">Brand / Recipient</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2 text-right">Weekly</th>
                <th className="px-3 py-2">Next due</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {plans.map((p: any) => {
                const bal = (p.total_amount_cents - p.paid_amount_cents) / 100;
                return (
                  <tr key={p.id}>
                    <td className="px-3 py-2 font-mono text-[11px]">{p.reference}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-navy">{p.client_brands?.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.recipient_name || "—"} · {p.recipient_email}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">R{(p.total_amount_cents / 100).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">R{(p.paid_amount_cents / 100).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-semibold">R{bal.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">R{(p.weekly_amount_cents / 100).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs">{p.next_due_date ?? "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        p.status === "settled" ? "bg-emerald-100 text-emerald-800"
                        : p.status === "cancelled" ? "bg-rose-100 text-rose-800"
                        : p.status === "paused" ? "bg-amber-100 text-amber-800"
                        : "bg-sky-100 text-sky-800"
                      }`}>{p.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {p.public_token && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => {
                              const url = `${window.location.origin}/installments/${p.reference}?t=${p.public_token}`;
                              navigator.clipboard?.writeText(url);
                              toast.success("Recipient pay link copied");
                            }}
                            title="Copy the recipient-facing payment link"
                          >
                            <LinkIcon className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {p.invoice_pdf_path && <InvoiceLinkButton planId={p.id} />}
                        <Button size="sm" variant="outline" onClick={() => setHistoryPlan(p)} title="View all payments">
                          <History className="mr-1 h-3.5 w-3.5" /> Payments
                        </Button>
                        {p.status !== "cancelled" && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={resendMut.isPending && resendMut.variables?.plan_id === p.id}
                            onClick={() => resendMut.mutate({ plan_id: p.id })}
                            title="Re-send this invoice to the client and BCC admin"
                          >
                            {resendMut.isPending && resendMut.variables?.plan_id === p.id ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Mail className="mr-1 h-3.5 w-3.5" />
                            )}
                            Re-send invoice
                          </Button>
                        )}
                        {p.status !== "settled" && (
                          <Button size="sm" onClick={() => { setOpenId(p.id); setAmount("50"); }} className="bg-navy hover:bg-navy/90">
                            Record payment
                          </Button>
                        )}
                        {p.status === "active" && (
                          <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ id: p.id, status: "paused" })}>
                            Pause
                          </Button>
                        )}
                        {p.status === "paused" && (
                          <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ id: p.id, status: "active" })}>
                            Resume
                          </Button>
                        )}
                        {p.status !== "cancelled" && p.status !== "settled" && (
                          <Button size="sm" variant="outline" onClick={() => setEditPlan(p)} title="Edit invoice">
                            <Pencil className="mr-1 h-3.5 w-3.5" />
                            Edit
                          </Button>
                        )}
                        {p.status !== "cancelled" && p.status !== "settled" && (
                          <Button size="sm" variant="ghost" onClick={() => statusMut.mutate({ id: p.id, status: "cancelled" })}>
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ClaimsPanel />
      <SendJobsPanel />


      {openId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOpenId(null)}>
          <div className="w-full max-w-md rounded-2xl bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg text-navy">Record payment</h3>
            <div className="mt-3 space-y-3">
              <div>
                <Label>Amount (R)</Label>
                <Input type="number" min={1} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
                <p className="mt-1 text-xs text-muted-foreground">Minimum weekly amount is R50.00.</p>
              </div>
              <div>
                <Label>Method</Label>
                <Select value={method} onValueChange={(v) => setMethod(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eft">EFT</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="payfast">PayFast</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. EFT proof received" />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpenId(null)}>Cancel</Button>
              <Button
                onClick={() => pay.mutate({ plan_id: openId, amount_zar: Number(amount), method, note })}
                disabled={pay.isPending || !(Number(amount) > 0)}
                className="bg-navy hover:bg-navy/90"
              >
                {pay.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {historyPlan && (
        <PaymentHistoryDialog plan={historyPlan} onClose={() => setHistoryPlan(null)} />
      )}

      {editPlan && (
        <EditPlanDialog plan={editPlan} onClose={() => setEditPlan(null)} />
      )}
    </section>
  );
}

function PaymentHistoryDialog({ plan, onClose }: { plan: any; onClose: () => void }) {
  const getPlan = useServerFn(adminGetInstallmentPlan);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-brand-installment-detail", plan.id],
    queryFn: () => getPlan({ data: { id: plan.id } }),
    refetchInterval: 15_000,
  });
  const detail: any = (data as any)?.plan ?? plan;
  const payments: any[] = (data as any)?.payments ?? [];
  const claims: any[] = (data as any)?.claims ?? [];
  const money = (c: number) => "R" + (((c ?? 0) as number) / 100).toFixed(2);
  const total = detail.total_amount_cents ?? 0;
  const paid = payments.reduce((s, x) => s + Number(x.amount_cents || 0), 0);
  const balance = Math.max(0, total - (detail.paid_amount_cents ?? paid));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={onClose}>
      <div className="my-8 w-full max-w-3xl rounded-2xl bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg text-navy">Payment history</h3>
            <p className="text-xs text-muted-foreground">
              <span className="font-mono">{detail.reference}</span> · {detail.client_brands?.name ?? plan.client_brands?.name} · {detail.recipient_name || "—"} ({detail.recipient_email})
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Total", money(total)],
            ["Paid", money(detail.paid_amount_cents ?? paid)],
            ["Balance", money(balance)],
            ["Payments", String(payments.length)],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-border bg-muted/40 p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</p>
              <p className="font-semibold text-navy">{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 grid gap-2 rounded-lg border border-border p-3 text-xs sm:grid-cols-3">
          <div><span className="text-muted-foreground">Status:</span> <strong>{detail.status}</strong></div>
          <div><span className="text-muted-foreground">Instalment:</span> {money(detail.weekly_amount_cents)}</div>
          <div><span className="text-muted-foreground">Next due:</span> {detail.next_due_date ?? "—"}</div>
          <div><span className="text-muted-foreground">Final due:</span> {detail.final_due_date ?? "—"}</div>
          <div><span className="text-muted-foreground">Total weeks:</span> {detail.total_weeks ?? "—"}</div>
          <div><span className="text-muted-foreground">Created:</span> {detail.created_at ? new Date(detail.created_at).toLocaleDateString() : "—"}</div>
        </div>

        {isLoading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading payments…
          </div>
        ) : payments.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No payments recorded yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Method</th>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payments.map((x: any, i: number) => (
                  <tr key={x.id}>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{payments.length - i}</td>
                    <td className="px-3 py-2 text-xs">
                      {x.paid_at ? new Date(x.paid_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{money(x.amount_cents)}</td>
                    <td className="px-3 py-2 text-xs uppercase">{x.method ?? "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{x.provider_ref ?? "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{x.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {claims.length > 0 && (
          <div className="mt-5">
            <h4 className="mb-2 font-display text-sm text-navy">Client payment claims</h4>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Submitted</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2">Payer</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {claims.map((c: any) => (
                    <tr key={c.id}>
                      <td className="px-3 py-2 text-xs">{c.created_at ? new Date(c.created_at).toLocaleString() : "—"}</td>
                      <td className="px-3 py-2 text-right">{money(c.amount_cents)}</td>
                      <td className="px-3 py-2 text-xs">{c.payer_email ?? "—"}</td>
                      <td className="px-3 py-2 text-xs capitalize">{c.status}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{c.note || c.reviewer_note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EditPlanDialog({ plan, onClose }: { plan: any; onClose: () => void }) {
  const update = useServerFn(adminUpdateInstallmentPlan);
  const qc = useQueryClient();
  const [form, setForm] = useState({
    recipient_name: plan.recipient_name ?? "",
    recipient_email: plan.recipient_email ?? "",
    recipient_phone: plan.client_phone ?? "",
    total_amount_zar: String((plan.total_amount_cents ?? 0) / 100),
    weekly_amount_zar: String((plan.weekly_amount_cents ?? 0) / 100),
    cadence: (Number(plan.installment_interval_days ?? 7) >= 28 ? "monthly" : "weekly") as
      | "weekly"
      | "monthly",
    next_due_date: plan.next_due_date ?? "",
    notes: plan.notes ?? "",
  });

  const mut = useMutation({
    mutationFn: () =>
      update({
        data: {
          plan_id: plan.id,
          recipient_name: form.recipient_name.trim(),
          recipient_email: form.recipient_email.trim(),
          recipient_phone: form.recipient_phone.trim(),
          total_amount_zar: Number(form.total_amount_zar) || undefined,
          weekly_amount_zar: Number(form.weekly_amount_zar) || undefined,
          cadence: form.cadence,
          next_due_date: form.next_due_date || undefined,
          notes: form.notes,
        } as any,
      }),
    onSuccess: () => {
      toast.success("Invoice updated");
      qc.invalidateQueries({ queryKey: ["admin-brand-installments"] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message ?? "Update failed"),
  });

  const paidZar = (plan.paid_amount_cents ?? 0) / 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg text-navy">Edit invoice · {plan.reference}</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Already paid: <strong>R{paidZar.toFixed(2)}</strong>. Changing the total, per-instalment
          amount, cadence, or next due date rebuilds the remaining schedule and final due date.
        </p>

        <div className="mt-4 grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1">
              <Label className="text-xs">Client name</Label>
              <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} />
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">Client email</Label>
              <Input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} />
            </div>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Phone (optional)</Label>
            <Input value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Payment frequency</Label>
            <div className="mt-1 inline-flex overflow-hidden rounded-md border border-input">
              <button type="button" onClick={() => setForm({ ...form, cadence: "weekly" })}
                className={`px-3 py-1.5 text-xs ${form.cadence === "weekly" ? "bg-navy text-white" : "bg-background text-navy hover:bg-accent"}`}>
                Weekly
              </button>
              <button type="button" onClick={() => setForm({ ...form, cadence: "monthly" })}
                className={`px-3 py-1.5 text-xs ${form.cadence === "monthly" ? "bg-navy text-white" : "bg-background text-navy hover:bg-accent"}`}>
                Monthly
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1">
              <Label className="text-xs">Total owing (R)</Label>
              <Input type="number" min={1} step="0.01" value={form.total_amount_zar} onChange={(e) => setForm({ ...form, total_amount_zar: e.target.value })} />
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">{form.cadence === "monthly" ? "Monthly" : "Weekly"} target (R)</Label>
              <Input type="number" min={50} step="0.01" value={form.weekly_amount_zar} onChange={(e) => setForm({ ...form, weekly_amount_zar: e.target.value })} />
            </div>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Next due date</Label>
            <Input type="date" value={form.next_due_date ?? ""} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Notes</Label>
            <textarea className="min-h-[70px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} className="bg-navy hover:bg-navy/90">
            {mut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save changes
          </Button>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Tip: after editing, use the “Re-send invoice” button on the row to email the updated schedule.
        </p>
      </div>
    </div>
  );
}

function InvoiceLinkButton({ planId }: { planId: string }) {
  const getUrl = useServerFn(adminGetPlanInvoiceUrl);
  const [loading, setLoading] = useState(false);
  return (
    <Button
      size="sm" variant="outline" disabled={loading}
      onClick={async () => {
        setLoading(true);
        try {
          const r = await getUrl({ data: { plan_id: planId } });
          if (r?.url) window.open(r.url, "_blank", "noopener");
          else toast.error("No invoice file");
        } catch (e: any) { toast.error(e?.message ?? "Failed"); }
        finally { setLoading(false); }
      }}
      title="Open invoice PDF"
    >
      <FileText className="h-3.5 w-3.5" />
    </Button>
  );
}

function ClaimsPanel() {
  const listClaims = useServerFn(adminListPendingClaims);
  const verify = useServerFn(adminVerifyClaim);
  const reject = useServerFn(adminRejectClaim);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-brand-claims"],
    queryFn: () => listClaims(),
  });
  const verifyMut = useMutation({
    mutationFn: (claim_id: string) => verify({ data: { claim_id } }),
    onSuccess: () => {
      toast.success("Claim verified");
      qc.invalidateQueries({ queryKey: ["admin-brand-claims"] });
      qc.invalidateQueries({ queryKey: ["admin-brand-installments"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });
  const rejectMut = useMutation({
    mutationFn: (claim_id: string) => reject({ data: { claim_id } }),
    onSuccess: () => {
      toast.success("Claim rejected");
      qc.invalidateQueries({ queryKey: ["admin-brand-claims"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="font-display text-base text-navy">Recipient EFT claims</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Recipients who submit their own EFT proof appear here. Verify to apply the payment and email a receipt.
      </p>
      {isLoading ? (
        <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
      ) : !data?.claims?.length ? (
        <p className="mt-3 text-sm text-muted-foreground">No pending claims.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5">When</th>
                <th className="px-2 py-1.5">Reference</th>
                <th className="px-2 py-1.5">Payer</th>
                <th className="px-2 py-1.5 text-right">Amount</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.claims.map((c: any) => (
                <tr key={c.id}>
                  <td className="px-2 py-1.5 text-xs">{new Date(c.created_at).toLocaleString("en-ZA")}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{c.brand_installment_plans?.reference}</td>
                  <td className="px-2 py-1.5">
                    <div>{c.payer_email}</div>
                    {c.note && <div className="text-[11px] text-muted-foreground">{c.note}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-right">R{(c.amount_cents / 100).toFixed(2)}</td>
                  <td className="px-2 py-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      c.status === "verified" ? "bg-emerald-100 text-emerald-800"
                      : c.status === "rejected" ? "bg-rose-100 text-rose-800"
                      : "bg-amber-100 text-amber-800"
                    }`}>{c.status}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {c.status === "pending" && (
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" className="bg-navy hover:bg-navy/90" disabled={verifyMut.isPending} onClick={() => verifyMut.mutate(c.id)}>Verify</Button>
                        <Button size="sm" variant="ghost" disabled={rejectMut.isPending} onClick={() => rejectMut.mutate(c.id)}>Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SendJobsPanel() {
  const listJobs = useServerFn(adminListSendJobs);
  const retry = useServerFn(adminRetrySendJob);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-brand-send-jobs"],
    queryFn: () => listJobs(),
    refetchInterval: 30_000,
  });
  const retryMut = useMutation({
    mutationFn: (job_id: string) => retry({ data: { job_id } }),
    onSuccess: () => {
      toast.success("Re-queued — the retry worker will pick it up shortly");
      qc.invalidateQueries({ queryKey: ["admin-brand-send-jobs"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="font-display text-base text-navy flex items-center gap-2">
        <Mail className="h-4 w-4 text-navy" /> Bulk-send delivery status
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Every bulk recipient email appears here. Failed sends retry automatically (up to 5 attempts with backoff).
      </p>
      {isLoading ? (
        <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
      ) : !data?.jobs?.length ? (
        <p className="mt-3 text-sm text-muted-foreground">No send jobs yet.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5">Created</th>
                <th className="px-2 py-1.5">Recipient</th>
                <th className="px-2 py-1.5">Brand</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5 text-right">Retries</th>
                <th className="px-2 py-1.5">Last attempt</th>
                <th className="px-2 py-1.5">Error</th>
                <th className="px-2 py-1.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.jobs.map((j: any) => (
                <tr key={j.id}>
                  <td className="px-2 py-1.5 text-xs">{new Date(j.created_at).toLocaleString("en-ZA")}</td>
                  <td className="px-2 py-1.5">{j.recipient_name || "—"} · <span className="text-xs">{j.recipient_email}</span></td>
                  <td className="px-2 py-1.5 text-xs">{j.client_brands?.name ?? "—"}</td>
                  <td className="px-2 py-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      j.status === "sent" ? "bg-emerald-100 text-emerald-800"
                      : j.status === "failed" ? "bg-rose-100 text-rose-800"
                      : "bg-sky-100 text-sky-800"
                    }`}>{j.status}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right">{j.retry_count ?? 0}</td>
                  <td className="px-2 py-1.5 text-xs">{j.last_attempt_at ? new Date(j.last_attempt_at).toLocaleString("en-ZA") : "—"}</td>
                  <td className="px-2 py-1.5 text-xs text-rose-700 max-w-[240px] truncate" title={j.error_message ?? ""}>{j.error_message ?? ""}</td>
                  <td className="px-2 py-1.5 text-right">
                    {j.status === "failed" && (
                      <Button size="sm" variant="outline" disabled={retryMut.isPending} onClick={() => retryMut.mutate(j.id)}>
                        <RefreshCw className="mr-1 h-3.5 w-3.5" /> Retry
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ManualClientButton() {
  const create = useServerFn(adminCreateManualClientPlan);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    brand_name: "",
    recipient_name: "",
    recipient_email: "",
    recipient_phone: "",
    initial_due_date: "",
    total_amount_zar: "150",
    weekly_amount_zar: "50",
    notes: "",
    send_email: true,
    cadence: "weekly" as "weekly" | "monthly",
  });
  const [result, setResult] = useState<{ pay_portal_url: string; reference: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      create({
        data: {
          brand_name: form.brand_name.trim(),
          recipient_name: form.recipient_name.trim(),
          recipient_email: form.recipient_email.trim(),
          recipient_phone: form.recipient_phone.trim(),
          initial_due_date: form.initial_due_date || undefined,
          total_amount_zar: Number(form.total_amount_zar) || 0,
          weekly_amount_zar: Number(form.weekly_amount_zar) || 50,
          notes: form.notes.trim(),
          send_email: form.send_email,
          payfast_enabled: true,
          cadence: form.cadence,
        } as any,
      }),
    onSuccess: async (r: any) => {
      toast.success(`Client registered · ${r.reference}`);
      setResult({ pay_portal_url: r.pay_portal_url, reference: r.reference });
      try {
        await navigator.clipboard.writeText(r.pay_portal_url);
        setCopied(true);
        toast.success("Pay link copied to clipboard");
      } catch {}
      qc.invalidateQueries({ queryKey: ["admin-brand-installments"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <>
      <Button size="sm" onClick={() => { setOpen(true); setResult(null); setCopied(false); }}>
        + Register client manually
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg rounded-xl bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg text-navy">Register a new client</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Creates a brand + installment plan and (optionally) emails the client a payment link they can use to pay any amount via PayFast.
            </p>

            {result ? (
              <div className="mt-4 space-y-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm">
                <p className="font-medium text-emerald-800">Plan {result.reference} created.</p>
                <p className="text-xs text-emerald-700">
                  {copied ? "✓ Pay link copied — share it with the client." : "Copy the link below and share it with the client."}
                </p>
                <div className="break-all rounded bg-white p-2 font-mono text-[11px]">{result.pay_portal_url}</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={async () => { await navigator.clipboard.writeText(result.pay_portal_url); setCopied(true); toast.success("Link copied"); }}>
                    {copied ? "Copied ✓" : "Copy pay link"}
                  </Button>
                  <a href={result.pay_portal_url} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs hover:bg-accent">
                    Open in new tab
                  </a>
                  {form.recipient_email ? (
                    <a href={`mailto:${form.recipient_email}?subject=Your%20payment%20link%20${encodeURIComponent(result.reference)}&body=${encodeURIComponent(result.pay_portal_url)}`} className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs hover:bg-accent">
                      Email client
                    </a>
                  ) : null}
                  <Button size="sm" onClick={() => setOpen(false)}>Done</Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 grid gap-3">
                <div className="grid gap-1">
                  <Label className="text-xs">Brand / Business name *</Label>
                  <Input value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} placeholder="e.g. Acme Trading (Pty) Ltd" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Client name *</Label>
                    <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="Jane Doe" />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">Client email *</Label>
                    <Input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} placeholder="jane@acme.co.za" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Phone (optional)</Label>
                    <Input value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} placeholder="0712345678" />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">First due date (optional)</Label>
                    <Input type="date" value={form.initial_due_date} onChange={(e) => setForm({ ...form, initial_due_date: e.target.value })} />
                  </div>
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Payment frequency</Label>
                  <div className="inline-flex overflow-hidden rounded-md border border-input">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, cadence: "weekly" })}
                      className={`px-3 py-1.5 text-xs ${form.cadence === "weekly" ? "bg-navy text-white" : "bg-background text-navy hover:bg-accent"}`}
                    >
                      Weekly
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, cadence: "monthly" })}
                      className={`px-3 py-1.5 text-xs ${form.cadence === "monthly" ? "bg-navy text-white" : "bg-background text-navy hover:bg-accent"}`}
                    >
                      Monthly
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Total owing (R) *</Label>
                    <Input type="number" min={10} step="0.01" value={form.total_amount_zar} onChange={(e) => setForm({ ...form, total_amount_zar: e.target.value })} />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">{form.cadence === "monthly" ? "Monthly" : "Weekly"} target (R)</Label>
                    <Input type="number" min={50} step="0.01" value={form.weekly_amount_zar} onChange={(e) => setForm({ ...form, weekly_amount_zar: e.target.value })} />
                  </div>
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Notes (shown in email)</Label>
                  <textarea className="min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} />
                  Email the pay link to the client now
                </label>
                <div className="flex justify-end gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button
                    size="sm"
                    disabled={
                      mut.isPending
                      || !form.brand_name.trim()
                      || !form.recipient_name.trim()
                      || !form.recipient_email.trim()
                      || Number(form.total_amount_zar) < 10
                    }
                    onClick={() => mut.mutate()}
                  >
                    {mut.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                    Create plan
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}


